// Modul belajar Matematika – elemen Geometri & Pengukuran.
// Format modul & langkah dijelaskan di src/belajar/README.md.
import { rectSVG, triBH, trapSVG, unitCubes, isoBox, dieSVG, denahTable, clockSVG, triAngles, lineAngle, svgWrap, tx } from '../../subjects/matematika/gambar.js';

// ---------- ikon kartu modul (64 × 64) ----------
const IKON = {
  kelilingLuas: '<svg viewBox="0 0 64 64"><rect x="8" y="12" width="48" height="40" rx="4" fill="var(--sky-soft)" stroke="var(--edge)" stroke-width="3"/><path d="M8 32H56M24 12V52M40 12V52" stroke="var(--sky)" stroke-width="2.5"/><rect x="8" y="12" width="48" height="40" rx="4" fill="none" stroke="var(--pink)" stroke-width="4" stroke-dasharray="7 4"/></svg>',
  volume: '<svg viewBox="0 0 64 64"><path d="M32 8L54 19V45L32 56L10 45V19Z" fill="var(--cube-l)" stroke="var(--edge)" stroke-width="3" stroke-linejoin="round"/><path d="M32 8L54 19L32 30L10 19Z" fill="var(--cube-top)" stroke="var(--edge)" stroke-width="3" stroke-linejoin="round"/><path d="M32 30V56L54 45V19Z" fill="var(--cube-r)" stroke="var(--edge)" stroke-width="3" stroke-linejoin="round"/><path d="M21 13.5L43 24.5M43 13.5L21 24.5M21 24.5V50.5M43 24.5V50.5" stroke="var(--edge)" stroke-width="1.5" opacity=".5"/></svg>',
  dadu: '<svg viewBox="0 0 64 64"><rect x="10" y="10" width="44" height="44" rx="10" fill="var(--surface)" stroke="var(--edge)" stroke-width="3" transform="rotate(-8 32 32)"/><g fill="var(--grape)"><circle cx="22" cy="22" r="4.5"/><circle cx="32" cy="32" r="4.5"/><circle cx="42" cy="42" r="4.5"/></g></svg>',
  satuan: '<svg viewBox="0 0 64 64"><rect x="6" y="22" width="52" height="20" rx="4" fill="var(--mint-soft)" stroke="var(--edge)" stroke-width="3" transform="rotate(-18 32 32)"/><g stroke="var(--edge)" stroke-width="2.5" transform="rotate(-18 32 32)"><path d="M14 22V30M22 22V28M30 22V32M38 22V28M46 22V30"/></g></svg>',
  jam: '<svg viewBox="0 0 64 64"><circle cx="32" cy="34" r="24" fill="var(--surface)" stroke="var(--edge)" stroke-width="3.5"/><path d="M32 34V18" stroke="var(--sky)" stroke-width="4" stroke-linecap="round"/><path d="M32 34L42 40" stroke="var(--pink)" stroke-width="5" stroke-linecap="round"/><circle cx="32" cy="34" r="3" fill="var(--edge)"/><path d="M24 6H40" stroke="var(--edge)" stroke-width="4" stroke-linecap="round"/></svg>',
  sudut: '<svg viewBox="0 0 64 64"><path d="M6 52A26 26 0 0 1 58 52Z" fill="var(--sky-soft)" stroke="var(--edge)" stroke-width="3"/><path d="M32 52L32 52L48 52A16 16 0 0 0 40 38Z" fill="var(--sun)" stroke="var(--orange)" stroke-width="2"/><path d="M8 52H58M32 52L46 28" stroke="var(--pink)" stroke-width="4" stroke-linecap="round"/></svg>',
  taksir: '<svg viewBox="0 0 64 64"><path d="M6 40H58" stroke="var(--edge)" stroke-width="3.5" stroke-linecap="round"/><path d="M10 34V46M32 36V44M54 34V46" stroke="var(--edge)" stroke-width="3" stroke-linecap="round"/><path d="M40 30L40 14L34 20M40 14L46 20" stroke="var(--pink)" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/><path d="M40 32L33 22H47Z" fill="var(--pink)" stroke="var(--edge)" stroke-width="2"/><path d="M34 40H54" stroke="var(--sun)" stroke-width="6" stroke-linecap="round" opacity=".7"/></svg>',
};

// Empat pilihan jaring-jaring kubus A–D (C bukan jaring-jaring kubus).
function pilihanJaring() {
  const s = 16, jaring = [
    [[1, 0], [0, 1], [1, 1], [2, 1], [1, 2], [1, 3]],
    [[0, 0], [0, 1], [1, 1], [2, 1], [3, 1], [3, 2]],
    [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1]],
    [[0, 0], [1, 0], [2, 0], [2, 1], [3, 1], [4, 1]],
  ];
  const lebar = 96;
  const isi = jaring.map((sel, i) => {
    const ox = 8 + i * lebar;
    return sel.map(([x, y]) => `<rect x="${ox + x * s}" y="${22 + y * s}" width="${s}" height="${s}" class="sv-c"/>`).join('') + tx(ox + 24, 14, 'ABCD'[i]);
  }).join('');
  return svgWrap(4 * lebar, 96, isi, 400, 'empat pilihan jaring-jaring A sampai D');
}

export const MODUL_GEOMETRI = [
  // ---------- 1. Keliling & luas ----------
  {
    id: 'keliling-luas', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'sky', menit: 10, ikon: IKON.kelilingLuas,
    judul: 'Keliling & luas bangun datar',
    ringkas: 'Ubah ukuran persegi panjang, buktikan luas segitiga, jajargenjang, dan trapesium.',
    topik: ['keliling-luas', 'luas-bangun', 'sifat-bangun-datar'],
    rumus: ['keliling-persegi-panjang', 'luas-persegi-panjang', 'luas-segitiga', 'luas-jajargenjang', 'luas-trapesium'],
    ringkasan: [
      'Keliling = panjang seluruh pinggiran. Persegi panjang: **K = 2 × (p + l)**, persegi: **K = 4 × s**.',
      'Luas = banyak kotak satuan di dalamnya. Persegi panjang: **L = p × l**, persegi: **L = s × s**.',
      'Segitiga: **L = ½ × alas × tinggi**, karena segitiga adalah setengah persegi panjang.',
      'Jajargenjang: **L = alas × tinggi**. Trapesium: **L = (a + b) × t : 2**.',
    ],
    langkah: [
      {
        label: 'Keliling & luas', judul: 'Pinggiran dan isi',
        narasi: '**Keliling** adalah panjang seluruh pinggiran bangun, seperti semut yang berjalan mengelilinginya. **Luas** adalah banyaknya kotak satuan yang menutupi bagian dalamnya.',
        tugas: 'Seret titik kuning (atau pakai tombol + dan −) sampai luasnya **12 kotak**.',
        widget: { tipe: 'petakPersegiPanjang', target: { luas: 12 }, maks: [10, 7], awal: [3, 2] },
      },
      {
        label: 'Luas sama, keliling beda', judul: 'Bentuk berbeda, luas sama',
        narasi: 'Persegi panjang 6 × 2 dan 4 × 3 sama-sama luasnya 12, tetapi kelilingnya berbeda! Keliling = 2 × (p + l), sedangkan luas = p × l.',
        tugas: 'Buat persegi panjang dengan luas **12 cm²** dan keliling **14 cm**.',
        widget: { tipe: 'petakPersegiPanjang', target: { luas: 12, keliling: 14 }, maks: [10, 7], awal: [6, 2] },
      },
      {
        jenis: 'kuis', judul: 'Memagari taman',
        soal: 'Taman Pak Budi berbentuk persegi panjang dengan panjang 9 m dan lebar 5 m. Ia ingin memasang pagar di sekelilingnya. Berapa meter panjang pagar yang dibutuhkan?',
        visual: () => rectSVG(9, 5, 'm'),
        isian: { jawaban: 28, satuan: 'm' },
        petunjuk: 'Pagar dipasang di pinggiran, jadi kita mencari keliling: 2 × (p + l).',
        bahas: 'Keliling = 2 × (9 + 5) = 2 × 14 = **28 m**.',
      },
      {
        label: 'Luas segitiga', judul: 'Segitiga adalah setengah persegi panjang',
        narasi: 'Setiap segitiga bisa ditaruh di dalam persegi panjang yang alas dan tingginya sama. Ternyata bagian di luar segitiga sama besar dengan segitiganya!',
        tugas: 'Tekan **Buktikan!** dan perhatikan dua potongan biru.',
        widget: { tipe: 'segitigaSetengah', alas: 8, tinggi: 5, puncak: 3 },
      },
      {
        jenis: 'kuis', judul: 'Luas segitiga',
        soal: 'Sebuah segitiga memiliki alas 10 cm dan tinggi 6 cm. Berapa luasnya?',
        visual: () => triBH(10, 6, 'cm'),
        isian: { jawaban: 30, satuan: 'cm²' },
        petunjuk: 'Luas segitiga = ½ × alas × tinggi.',
        bahas: '½ × 10 × 6 = ½ × 60 = **30 cm²**.',
      },
      {
        label: 'Luas jajargenjang', judul: 'Potong, geser, jadi persegi panjang',
        narasi: 'Jajargenjang bisa dipotong di garis tingginya, lalu potongannya digeser ke sisi lain. Hasilnya persegi panjang dengan alas dan tinggi yang sama!',
        tugas: 'Tekan **Potong & geser**.',
        widget: { tipe: 'bangunGeser', mode: 'jajargenjang', alas: 6, tinggi: 4, geser: 2 },
      },
      {
        label: 'Luas trapesium', judul: 'Dua trapesium kembar',
        narasi: 'Trapesium punya dua sisi sejajar, misalnya **a** dan **b**. Kalau digandakan lalu diputar setengah lingkaran, dua trapesium membentuk jajargenjang beralas **a + b**.',
        tugas: 'Tekan **Gandakan & putar**.',
        widget: { tipe: 'bangunGeser', mode: 'trapesium', atas: 4, bawah: 8, tinggi: 4 },
      },
      {
        jenis: 'kuis', judul: 'Luas trapesium',
        soal: 'Sebuah trapesium memiliki sisi sejajar 6 cm dan 10 cm, dengan tinggi 5 cm. Luasnya adalah ....',
        visual: () => trapSVG(6, 10, 5, 'cm'),
        pilihan: ['40 cm²', '80 cm²', '30 cm²', '21 cm²'], kunci: 0,
        petunjuk: 'Jumlahkan kedua sisi sejajar, kalikan dengan tinggi, lalu bagi 2.',
        bahas: '(6 + 10) × 5 : 2 = 16 × 5 : 2 = 80 : 2 = **40 cm²**.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: 'Luas persegi bersisi 7 cm adalah 49 cm².', benar: true },
          { teks: 'Keliling persegi panjang 8 cm × 3 cm adalah 24 cm.', benar: false },
          { teks: 'Luas jajargenjang beralas 9 cm dan tinggi 4 cm adalah 36 cm².', benar: true },
        ],
        petunjuk: 'Hitung satu per satu dengan rumusnya: s × s, 2 × (p + l), dan alas × tinggi.',
        bahas: '7 × 7 = 49 cm² (benar). Keliling = 2 × (8 + 3) = **22 cm**, bukan 24 (salah). 9 × 4 = 36 cm² (benar).',
      },
    ],
  },

  // ---------- 2. Volume ----------
  {
    id: 'volume', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'orange', menit: 8, ikon: IKON.volume,
    judul: 'Volume kubus & balok',
    ringkas: 'Susun kubus satuan lapis demi lapis untuk menemukan rumus volume.',
    topik: ['volume', 'kubus-satuan'],
    rumus: ['volume-kubus', 'volume-balok', 'liter-dm3'],
    ringkasan: [
      'Volume = banyak kubus satuan yang mengisi bangun ruang.',
      'Balok: **V = p × l × t** (satu lapisan p × l, dikali banyak lapisan).',
      'Kubus: **V = s × s × s**, karena semua rusuknya sama panjang.',
      '**1 liter = 1 dm³ = 1.000 cm³**, dan 1 ml = 1 cm³.',
    ],
    langkah: [
      {
        label: 'Apa itu volume?', judul: 'Berapa kubus yang muat?',
        narasi: '**Volume** adalah besarnya ruang yang diisi sebuah benda. Kita mengukurnya dengan **kubus satuan**, yaitu kubus kecil yang rusuknya 1.',
        tugas: 'Atur panjang, lebar, dan tinggi sampai balok tersusun dari **12** kubus satuan.',
        widget: { tipe: 'kubusSatuan', target: { volume: 12 }, maks: [5, 4, 4], awal: [2, 2, 1] },
      },
      {
        label: 'Hitung per lapisan', judul: 'Lapisan demi lapisan',
        narasi: 'Satu lapisan berisi **panjang × lebar** kubus. Kalau ada beberapa lapisan, kalikan lagi dengan **tinggi**. Tekan **Hitung per lapisan** untuk melihatnya.',
        tugas: 'Buat balok bervolume **24** yang tingginya **2** lapisan.',
        widget: { tipe: 'kubusSatuan', target: { volume: 24, t: 2 }, maks: [6, 4, 4], awal: [2, 2, 1] },
      },
      {
        jenis: 'kuis', judul: 'Hitung kubusnya',
        soal: 'Berapa banyak kubus satuan pada susunan di bawah ini?',
        visual: () => unitCubes(4, 3, 2),
        isian: { jawaban: 24, satuan: 'kubus' },
        petunjuk: 'Hitung satu lapisan dulu (panjang × lebar), lalu kalikan dengan banyak lapisan.',
        bahas: 'Satu lapisan 4 × 3 = 12 kubus, ada 2 lapisan, jadi 12 × 2 = **24** kubus satuan.',
      },
      {
        label: 'Volume kubus', judul: 'Kubus: semua rusuknya sama',
        narasi: 'Kubus adalah balok istimewa karena panjang, lebar, dan tingginya sama. Jadi volume kubus = **s × s × s**.',
        tugas: 'Buat kubus yang volumenya **27** kubus satuan.',
        widget: { tipe: 'kubusSatuan', kubus: true, target: { volume: 27 }, maks: [5], awal: [2] },
      },
      {
        jenis: 'kuis', judul: 'Kotak pensil',
        soal: 'Sebuah kotak pensil berbentuk balok berukuran panjang 8 cm, lebar 5 cm, dan tinggi 3 cm. Volumenya adalah ....',
        visual: () => isoBox(8, 5, 3, 'cm'),
        pilihan: ['120 cm³', '16 cm³', '40 cm³', '158 cm³'], kunci: 0,
        petunjuk: 'Volume balok = p × l × t.',
        bahas: '8 × 5 × 3 = 40 × 3 = **120 cm³**.',
      },
      {
        label: 'Liter', judul: 'Volume dan liter', mood: 'wow',
        narasi: 'Untuk benda cair, volume sering ditulis dalam **liter**. Ingat: **1 liter = 1 dm³ = 1.000 cm³**. Akuarium 50 cm × 30 cm × 40 cm berisi 60.000 cm³ air, yaitu **60 liter**.',
        visual: () => isoBox(50, 30, 40, 'cm'),
      },
      {
        jenis: 'kuis', judul: 'Kubus mainan',
        soal: 'Sebuah kubus mainan memiliki panjang rusuk 6 cm. Berapa volumenya?',
        isian: { jawaban: 216, satuan: 'cm³' },
        petunjuk: 'Volume kubus = s × s × s.',
        bahas: '6 × 6 × 6 = 36 × 6 = **216 cm³**.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: 'Balok 5 cm × 4 cm × 2 cm volumenya 40 cm³.', benar: true },
          { teks: 'Kubus dengan rusuk 3 cm volumenya 9 cm³.', benar: false },
          { teks: '2 liter sama dengan 2.000 cm³.', benar: true },
        ],
        petunjuk: 'Ingat: kubus dikali tiga kali (s × s × s), dan 1 liter = 1.000 cm³.',
        bahas: '5 × 4 × 2 = 40 cm³ (benar). 3 × 3 × 3 = **27 cm³**, bukan 9 (salah). 2 × 1.000 = 2.000 cm³ (benar).',
      },
    ],
  },

  // ---------- 3. Bangun ruang: jaring-jaring & tampak ----------
  {
    id: 'bangun-ruang', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'grape', menit: 9, ikon: IKON.dadu,
    judul: 'Bangun ruang: jaring-jaring & tampak',
    ringkas: 'Lipat jaring-jaring dadu dalam 3D dan gambar tampak atas & depan tumpukan kubus.',
    topik: ['tampak-bangun', 'dadu'],
    rumus: ['tampak-bangun', 'dadu'],
    ringkasan: [
      'Jaring-jaring kubus terdiri dari **6 persegi** yang bisa dilipat tanpa menumpuk.',
      'Sisi dadu yang berhadapan berjumlah **7**: 1 dan 6, 2 dan 5, 3 dan 4.',
      '**Tampak atas**: petak yang ada kubusnya. **Tampak depan**: tumpukan tertinggi di setiap kolom.',
      'Banyak kubus pada denah = jumlah semua angka di denah.',
    ],
    langkah: [
      {
        label: 'Jaring-jaring', judul: 'Kubus dari selembar kertas',
        narasi: '**Jaring-jaring** adalah bangun ruang yang dibuka dan dibentangkan. Kubus punya 6 sisi persegi, jadi jaring-jaringnya terdiri dari 6 persegi.',
        tugas: 'Lipat jaring-jaringnya, putar kubusnya, lalu jawab: angka berapa yang berhadapan dengan **2**?',
        widget: { tipe: 'jaringKubus', tanya: 2 },
      },
      {
        label: 'Rahasia dadu', judul: 'Sisi berhadapan berjumlah 7',
        narasi: 'Pada dadu, sisi yang berhadapan selalu berjumlah **7**: 1 dengan 6, 2 dengan 5, dan 3 dengan 4. Kalau kamu tahu angka di atas, angka di bawahnya adalah 7 dikurangi angka itu.',
        visual: () => dieSVG(1, 2, 3),
      },
      {
        jenis: 'kuis', judul: 'Sisi bawah dadu',
        soal: 'Perhatikan dadu di bawah. Angka pada sisi **bawah** dadu adalah ....',
        visual: () => dieSVG(5, 3, 1),
        pilihan: ['2', '4', '6', '3'], kunci: 0,
        petunjuk: 'Sisi bawah berhadapan dengan sisi atas. Jumlahnya 7.',
        bahas: 'Sisi atas menunjukkan 5, maka sisi bawah = 7 − 5 = **2**.',
      },
      {
        jenis: 'kuis', judul: 'Jaring-jaring yang salah',
        soal: 'Manakah yang **bukan** jaring-jaring kubus?',
        visual: pilihanJaring,
        pilihan: ['A', 'B', 'C', 'D'], kunci: 2,
        petunjuk: 'Bayangkan setiap gambar dilipat. Apakah ada dua persegi yang menumpuk?',
        bahas: 'Jaring C berbentuk 2 baris × 3 kolom. Kalau dilipat, beberapa persegi akan **menumpuk** dan ada sisi yang tetap terbuka, jadi tidak bisa menjadi kubus.',
      },
      {
        label: 'Tampak bangun', judul: 'Dilihat dari atas dan dari depan',
        narasi: 'Tumpukan kubus terlihat berbeda dari arah yang berbeda. **Tampak atas** menunjukkan petak mana yang ada kubusnya, sedangkan **tampak depan** menunjukkan kubus tertinggi di setiap kolom.',
        tugas: 'Ketuk kotak-kotak untuk menggambar **tampak atas** dan **tampak depan**, lalu tekan Periksa.',
        widget: { tipe: 'tampakBangun', hm: [[2, 1, 1], [1, 0, 2]], tampak: ['atas', 'depan'] },
      },
      {
        jenis: 'kuis', judul: 'Membaca denah',
        soal: 'Denah di bawah menunjukkan banyak kubus pada setiap petak (dilihat dari atas). Berapa banyak kubus seluruhnya?',
        visual: () => denahTable([[3, 2, 1], [2, 1, 1]]),
        isian: { jawaban: 10, satuan: 'kubus' },
        petunjuk: 'Setiap angka adalah tinggi tumpukan di petak itu. Jumlahkan semuanya.',
        bahas: '3 + 2 + 1 + 2 + 1 + 1 = **10** kubus.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: 'Kubus mempunyai 6 sisi berbentuk persegi.', benar: true },
          { teks: 'Pada dadu, angka 3 berhadapan dengan angka 5.', benar: false },
          { teks: 'Tampak atas tumpukan kubus menunjukkan tinggi setiap tumpukan.', benar: false },
        ],
        petunjuk: 'Ingat aturan jumlah 7 pada dadu, dan apa yang terlihat dari atas.',
        bahas: 'Kubus punya 6 sisi persegi (benar). 3 berhadapan dengan **4** karena 3 + 4 = 7 (salah). Dari atas kita hanya melihat petak mana yang ada kubusnya, **bukan** tingginya (salah).',
      },
    ],
  },

  // ---------- 4. Satuan panjang, berat & volume ----------
  {
    id: 'satuan-ukur', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'mint', menit: 9, ikon: IKON.satuan,
    judul: 'Satuan panjang, berat & volume',
    ringkas: 'Naik turun tangga satuan: kali 10 atau bagi 10 di setiap anak tangga.',
    topik: ['satuan-panjang', 'satuan-berat', 'satuan-volume', 'muat-wadah'],
    rumus: ['satuan-panjang', 'satuan-berat', 'satuan-volume', 'liter-dm3', 'patokan-satuan'],
    ringkasan: [
      'Tangga satuan: **turun 1 anak tangga dikali 10**, **naik 1 anak tangga dibagi 10**.',
      'Panjang: km – hm – dam – m – dm – cm – mm. Berat: kg – hg – dag – g – dg – cg – mg.',
      '1 ton = 1.000 kg dan 1 kuintal = 100 kg.',
      'Volume: kl – hl – dal – l – dl – cl – ml, dengan **1 liter = 1 dm³** dan **1 ml = 1 cm³**.',
    ],
    langkah: [
      {
        label: 'Tangga satuan panjang', judul: 'Naik turun tangga satuan',
        narasi: 'Satuan panjang tersusun seperti tangga: km, hm, dam, m, dm, cm, mm. Setiap **turun** satu anak tangga dikali 10, setiap **naik** satu anak tangga dibagi 10.',
        tugas: 'Pindahkan token (tombol atau ketuk anak tangga) untuk menjawab kedua soal.',
        widget: { tipe: 'tanggaSatuan', jenis: 'panjang', soal: [{ nilai: 3, dari: 'm', ke: 'cm' }, { nilai: 2500, dari: 'm', ke: 'km' }] },
      },
      {
        jenis: 'kuis', judul: 'Kilometer ke meter',
        soal: '4,5 km = ... m',
        isian: { jawaban: 4500, satuan: 'm' },
        petunjuk: 'Dari km ke m turun 3 anak tangga: dikali 10 × 10 × 10 = 1.000.',
        bahas: '4,5 × 1.000 = **4.500 m**.',
      },
      {
        label: 'Satuan berat', judul: 'Tangga satuan berat',
        narasi: 'Berat juga punya tangga: kg, hg, dag, g, dg, cg, mg. Ada juga **ton** dan **kuintal**: 1 ton = 1.000 kg dan 1 kuintal = 100 kg.',
        tugas: 'Jawab kedua soal dengan memindahkan token.',
        widget: { tipe: 'tanggaSatuan', jenis: 'berat', soal: [{ nilai: 2, dari: 'kg', ke: 'g' }, { nilai: 750, dari: 'g', ke: 'hg' }] },
      },
      {
        jenis: 'kuis', judul: 'Hasil panen',
        soal: 'Pak Tani memanen 3 kuintal jagung dan 50 kg kacang. Berat seluruh hasil panennya adalah ....',
        pilihan: ['350 kg', '53 kg', '3.050 kg', '80 kg'], kunci: 0,
        petunjuk: 'Ubah kuintal ke kg dulu: 1 kuintal = 100 kg.',
        bahas: '3 kuintal = 300 kg. 300 kg + 50 kg = **350 kg**.',
      },
      {
        label: 'Satuan volume', judul: 'Liter dan kawan-kawannya',
        narasi: 'Untuk benda cair kita memakai kl, hl, dal, l, dl, cl, ml. Ingat juga: **1 liter = 1 dm³** dan **1 ml = 1 cm³**.',
        tugas: 'Jawab kedua soal dengan memindahkan token.',
        widget: { tipe: 'tanggaSatuan', jenis: 'volume', soal: [{ nilai: 1.5, dari: 'l', ke: 'ml' }, { nilai: 4, dari: 'dal', ke: 'l' }] },
      },
      {
        jenis: 'kuis', judul: 'Isi botol',
        soal: 'Sebuah botol berisi 2.500 cm³ air. Berapa liter isinya?',
        isian: { jawaban: 2.5, satuan: 'liter' },
        petunjuk: '1 cm³ = 1 ml. Dari ml ke liter naik 3 anak tangga: dibagi 1.000.',
        bahas: '2.500 cm³ = 2.500 ml = 2.500 : 1.000 = **2,5 liter**.',
      },
      {
        label: 'Perkiraan', judul: 'Patokan ukuran sehari-hari', mood: 'think',
        narasi: 'Biasakan punya patokan: panjang pensil sekitar **15 cm**, tinggi pintu sekitar **2 m**, berat sebuah apel sekitar **200 g**, dan segelas air sekitar **250 ml**. Patokan membantumu memilih satuan yang masuk akal.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: '1 m = 100 cm', benar: true },
          { teks: '5 kg = 500 g', benar: false },
          { teks: '1 ton = 10 kuintal', benar: true },
        ],
        petunjuk: 'Hitung berapa anak tangga yang dilewati, lalu kalikan 10 sebanyak itu.',
        bahas: 'm ke cm turun 2 anak tangga: 1 × 100 = 100 cm (benar). kg ke g turun 3 anak tangga: 5 × 1.000 = **5.000 g** (salah). 1 ton = 1.000 kg = 10 × 100 kg = 10 kuintal (benar).',
      },
    ],
  },

  // ---------- 5. Waktu & kecepatan ----------
  {
    id: 'waktu-kecepatan', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'sun', menit: 10, ikon: IKON.jam,
    judul: 'Waktu & kecepatan',
    ringkas: 'Putar jarum jam, hitung lama waktu, dan jalankan mobil: jarak = kecepatan × waktu.',
    topik: ['waktu', 'kecepatan', 'tepat-waktu'],
    rumus: ['satuan-waktu', 'kecepatan'],
    ringkasan: [
      'Jarum pendek menunjuk **jam**, jarum panjang menunjuk **menit**. 1 jam = 60 menit, 1 menit = 60 detik.',
      'Waktu selesai = waktu mulai + lamanya kegiatan. Kalau menitnya lewat 60, jadikan 1 jam.',
      '**J = K × W**, **K = J : W**, dan **W = J : K**.',
    ],
    langkah: [
      {
        label: 'Membaca jam', judul: 'Jarum pendek dan jarum panjang',
        narasi: '**Jarum pendek** menunjukkan jam, **jarum panjang** menunjukkan menit. Setiap angka pada jam berarti 5 menit untuk jarum panjang.',
        tugas: 'Seret jarum panjang (atau pakai tombol) sampai jam menunjukkan pukul **07.45**.',
        widget: { tipe: 'jamAnalog', target: [7, 45], awal: [7, 0] },
      },
      {
        label: 'Lama waktu', judul: 'Menghitung waktu selesai',
        narasi: 'Untuk mencari waktu selesai, tambahkan lamanya kegiatan ke waktu mulai. Satu putaran penuh jarum panjang = **60 menit = 1 jam**.',
        tugas: 'Rapat mulai pukul 08.30 dan berlangsung 1 jam 35 menit. Majukan jam sampai waktu rapat selesai.',
        widget: { tipe: 'jamAnalog', mulai: [8, 30], durasi: 95 },
      },
      {
        jenis: 'kuis', judul: 'Jam ke menit',
        soal: 'Lomba lari berlangsung selama 2 jam 15 menit. Berapa menit lamanya?',
        pilihan: ['135 menit', '215 menit', '120 menit', '75 menit'], kunci: 0,
        petunjuk: '1 jam = 60 menit, jadi 2 jam = 120 menit.',
        bahas: '2 × 60 + 15 = 120 + 15 = **135 menit**.',
      },
      {
        label: 'Kecepatan', judul: 'Seberapa jauh dalam 1 jam?',
        narasi: '**Kecepatan** 50 km/jam artinya dalam 1 jam kendaraan menempuh 50 km. Kalau berjalan 3 jam, jaraknya 50 × 3 = 150 km.',
        tugas: 'Atur kecepatan dan waktu supaya mobil tiba **tepat** di kota yang berjarak 150 km.',
        widget: { tipe: 'perjalanan', target: 150, kecepatan: 40, waktu: 2 },
      },
      {
        label: 'Segitiga ajaib', judul: 'Rumus J, K, dan W',
        narasi: 'Jarak (J), kecepatan (K), dan waktu (W) saling berhubungan. Tutup huruf yang ingin dicari pada segitiga, maka sisanya menunjukkan rumusnya.',
        tugas: 'Ketuk ketiga huruf J, K, dan W satu per satu.',
        widget: { tipe: 'segitigaJKW', contoh: { J: 120, K: 40, W: 3 } },
      },
      {
        jenis: 'kuis', judul: 'Kecepatan bus',
        soal: 'Sebuah bus menempuh jarak 240 km dalam waktu 4 jam. Berapa kecepatan rata-rata bus itu?',
        isian: { jawaban: 60, satuan: 'km/jam' },
        petunjuk: 'Kecepatan = jarak : waktu.',
        bahas: 'K = 240 : 4 = **60 km/jam**.',
      },
      {
        jenis: 'kuis', judul: 'Kapan tiba?',
        soal: 'Ayah berangkat pukul 07.15 dengan kecepatan rata-rata 60 km/jam. Jarak ke kota tujuan 180 km. Ayah tiba pukul ....',
        pilihan: ['10.15', '09.15', '10.45', '08.15'], kunci: 0,
        petunjuk: 'Cari waktu tempuh dulu: W = J : K.',
        bahas: 'W = 180 : 60 = 3 jam. Pukul 07.15 + 3 jam = pukul **10.15**.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: '1 jam = 3.600 detik', benar: true },
          { teks: 'Mobil berkecepatan 45 km/jam selama 2 jam menempuh 90 km.', benar: true },
          { teks: 'Pukul 09.50 ditambah 20 menit adalah pukul 09.70.', benar: false },
        ],
        petunjuk: 'Ingat, menit tidak pernah lebih dari 59. Kalau sampai 60, jamnya bertambah.',
        bahas: '60 × 60 = 3.600 detik (benar). 45 × 2 = 90 km (benar). 09.50 + 20 menit = 09.70, padahal 60 menit = 1 jam, jadi menjadi **10.10** (salah).',
      },
    ],
  },

  // ---------- 6. Sudut ----------
  {
    id: 'sudut', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'pink', menit: 9, ikon: IKON.sudut,
    judul: 'Sudut',
    ringkas: 'Ukur sudut dengan busur, sobek pojok segitiga, dan temukan sudut jarum jam.',
    topik: ['sudut'],
    rumus: ['sudut-lurus', 'sudut-segitiga', 'sudut-jam'],
    ringkasan: [
      'Lancip: kurang dari 90°. Siku-siku: tepat 90°. Tumpul: antara 90° dan 180°. Lurus: 180°.',
      'Sudut-sudut pada garis lurus berjumlah **180°**.',
      'Jumlah ketiga sudut segitiga selalu **180°**.',
      'Pada jam, jarak antar angka **30°** (360° : 12).',
    ],
    langkah: [
      {
        label: 'Mengenal sudut', judul: 'Membuka dan menutup sudut',
        narasi: 'Sudut terbentuk dari dua sinar yang bertemu di satu titik. Besarnya diukur dengan **busur derajat** dalam satuan derajat (°).',
        tugas: 'Geser sinar merah untuk membuat sudut **90°**, lalu **135°**.',
        widget: { tipe: 'busurSudut', target: [90, 135] },
      },
      {
        jenis: 'kuis', judul: 'Jenis sudut',
        soal: 'Sudut yang besarnya 120° termasuk sudut ....',
        pilihan: ['tumpul', 'lancip', 'siku-siku', 'lurus'], kunci: 0,
        petunjuk: 'Bandingkan dengan 90° (siku-siku) dan 180° (lurus).',
        bahas: '120° lebih dari 90° tetapi kurang dari 180°, jadi termasuk sudut **tumpul**.',
      },
      {
        label: 'Garis lurus', judul: 'Sudut pada garis lurus',
        narasi: 'Garis lurus membentuk sudut **180°**. Kalau sebuah sinar membagi garis lurus menjadi dua sudut, jumlah kedua sudut itu selalu 180°.',
        tugas: 'Atur sinar supaya sudut ungu menjadi **130°**.',
        widget: { tipe: 'busurSudut', mode: 'lurus', pelurus: 130 },
      },
      {
        jenis: 'kuis', judul: 'Pasangan di garis lurus',
        soal: 'Dua sudut berada pada satu garis lurus. Jika sudut yang satu 65°, berapa besar sudut yang lain?',
        visual: () => lineAngle(65),
        isian: { jawaban: 115, satuan: '°' },
        petunjuk: 'Jumlah sudut pada garis lurus = 180°.',
        bahas: '180° − 65° = **115°**.',
      },
      {
        label: 'Sudut segitiga', judul: 'Tiga sudut, satu garis lurus',
        narasi: 'Kalau ketiga pojok segitiga disobek lalu disusun berdampingan, ketiganya membentuk garis lurus. Itu sebabnya jumlah sudut segitiga selalu **180°**.',
        tugas: 'Tekan **Sobek & susun**, lalu coba juga segitiga lain.',
        widget: { tipe: 'sobekSegitiga' },
      },
      {
        jenis: 'kuis', judul: 'Sudut ketiga',
        soal: 'Dua sudut sebuah segitiga besarnya 50° dan 60°. Berapa besar sudut ketiga?',
        visual: () => triAngles(50, 60),
        isian: { jawaban: 70, satuan: '°' },
        petunjuk: 'Kurangi 180° dengan kedua sudut yang diketahui.',
        bahas: '180° − 50° − 60° = **70°**.',
      },
      {
        label: 'Sudut jarum jam', judul: 'Setiap angka berjarak 30°',
        narasi: 'Satu putaran jam = 360° dibagi 12 angka, jadi jarak antar angka **30°**. Pada pukul 03.00 kedua jarum membentuk sudut 3 × 30° = 90°.',
        tugas: 'Atur jam ke pukul **04.00** dan lihat besar sudutnya.',
        widget: { tipe: 'jamAnalog', target: [4, 0], awal: [1, 0], tampilSudut: true },
      },
      {
        jenis: 'kuis', judul: 'Sudut pukul 05.00',
        soal: 'Besar sudut terkecil yang dibentuk jarum jam dan jarum menit pada pukul 05.00 adalah ....',
        visual: () => clockSVG(5, 0),
        pilihan: ['150°', '120°', '100°', '180°'], kunci: 0,
        petunjuk: 'Hitung berapa jarak angka di antara kedua jarum, lalu kalikan 30°.',
        bahas: 'Dari angka 12 ke angka 5 ada 5 jarak angka, jadi 5 × 30° = **150°**.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: 'Sudut siku-siku besarnya 90°.', benar: true },
          { teks: 'Sebuah segitiga bisa punya dua sudut tumpul.', benar: false },
          { teks: 'Sudut 45° termasuk sudut lancip.', benar: true },
        ],
        petunjuk: 'Ingat, jumlah sudut segitiga hanya 180°.',
        bahas: 'Siku-siku = 90° (benar). Dua sudut tumpul saja sudah lebih dari 180°, jadi **tidak mungkin** (salah). 45° kurang dari 90°, jadi lancip (benar).',
      },
    ],
  },

  // ---------- 7. Penaksiran & pembagian bersisa ----------
  {
    id: 'penaksiran', mapel: 'matematika', elemen: 'Geometri & Pengukuran', warna: 'sky', menit: 8, ikon: IKON.taksir,
    judul: 'Penaksiran, pembulatan & pembagian bersisa',
    ringkas: 'Bulatkan bilangan di garis bilangan dan potong pita untuk mengenal sisa pembagian.',
    topik: ['penaksiran', 'pembagian-bersisa'],
    rumus: ['pembulatan', 'pembagian-bersisa', 'patokan-satuan'],
    ringkasan: [
      'Pembulatan ke puluhan: lihat angka satuan. **5 ke atas dibulatkan naik**, di bawah 5 dibulatkan turun.',
      'Pembulatan ke ratusan: lihat angka puluhan (50 ke atas dibulatkan naik).',
      'Menaksir hasil hitung: bulatkan dulu setiap bilangan, baru dihitung.',
      'Pembagian bersisa: **yang dibagi = pembagi × hasil + sisa**, dan sisa selalu lebih kecil dari pembagi.',
    ],
    langkah: [
      {
        label: 'Pembulatan', judul: 'Ke puluhan terdekat',
        narasi: 'Membulatkan artinya mencari bilangan bulat puluhan yang **paling dekat**. Lihat angka satuannya: 5 ke atas dibulatkan ke atas, di bawah 5 dibulatkan ke bawah.',
        tugas: 'Bulatkan ketiga bilangan ke puluhan terdekat.',
        widget: { tipe: 'pembulatan', ke: 10, soal: [347, 352, 365] },
      },
      {
        label: 'Ke ratusan', judul: 'Ke ratusan terdekat',
        narasi: 'Untuk membulatkan ke ratusan, lihat angka **puluhan**nya. Kalau 50 ke atas, dibulatkan ke atas.',
        tugas: 'Bulatkan ketiga bilangan ke ratusan terdekat.',
        widget: { tipe: 'pembulatan', ke: 100, soal: [462, 1250, 849] },
      },
      {
        jenis: 'kuis', judul: 'Menaksir penjumlahan',
        soal: 'Hasil taksiran dari 398 + 512 (setiap bilangan dibulatkan ke ratusan terdekat) adalah ....',
        pilihan: ['900', '800', '1.000', '910'], kunci: 0,
        petunjuk: 'Bulatkan setiap bilangan dulu, baru dijumlahkan.',
        bahas: '398 dibulatkan menjadi 400 dan 512 menjadi 500, jadi 400 + 500 = **900**.',
      },
      {
        jenis: 'kuis', judul: 'Perkiraan ukuran',
        soal: 'Tinggi pintu kelasmu kira-kira ....',
        pilihan: ['2 m', '20 cm', '20 m', '2 km'], kunci: 0,
        petunjuk: 'Bandingkan dengan tinggi orang dewasa, sekitar 1,7 m.',
        bahas: 'Pintu sedikit lebih tinggi dari orang dewasa, jadi tingginya sekitar **2 m**.',
      },
      {
        label: 'Pembagian bersisa', judul: 'Memotong pita',
        narasi: 'Kadang pembagian tidak habis dan ada **sisa**. Misalnya pita 100 cm dipotong 15 cm-an: berapa potong yang didapat, dan berapa sisanya?',
        tugas: 'Potong pitanya sampai tidak bisa dipotong lagi.',
        widget: { tipe: 'potongPita', panjang: 100, potong: 15 },
      },
      {
        jenis: 'kuis', judul: 'Pita hiasan',
        soal: 'Ibu punya pita 250 cm. Pita itu dipotong-potong sepanjang 30 cm untuk hiasan. Berapa potong hiasan yang didapat?',
        isian: { jawaban: 8, satuan: 'potong' },
        petunjuk: 'Berapa kali 30 muat di 250? Coba hitung 30 × 8 dan 30 × 9.',
        bahas: '30 × 8 = 240 dan 30 × 9 = 270 (terlalu panjang). Jadi didapat **8 potong**.',
      },
      {
        jenis: 'kuis', judul: 'Sisa pita',
        soal: 'Dari pita 250 cm tadi (dipotong 30 cm-an), berapa cm sisa pitanya?',
        isian: { jawaban: 10, satuan: 'cm' },
        petunjuk: '8 potong × 30 cm = 240 cm sudah terpakai.',
        bahas: '250 − 240 = **10 cm**. Jadi 250 : 30 = 8 sisa 10.',
      },
      {
        jenis: 'kuis', judul: 'Benar atau salah?',
        soal: 'Tentukan **Benar** atau **Salah** untuk setiap pernyataan.',
        bs: [
          { teks: '763 dibulatkan ke puluhan terdekat menjadi 760.', benar: true },
          { teks: '1.450 dibulatkan ke ratusan terdekat menjadi 1.400.', benar: false },
          { teks: '47 : 5 = 9 sisa 2', benar: true },
        ],
        petunjuk: 'Untuk ratusan, lihat angka puluhannya. Untuk pembagian, cek: pembagi × hasil + sisa.',
        bahas: 'Satuan 3 < 5, jadi 760 (benar). Puluhan 50 → dibulatkan naik menjadi **1.500** (salah). 5 × 9 + 2 = 47 (benar).',
      },
    ],
  },
];
